import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { childAPI, parentDeviceAPI } from '../../api/client';
import T from '../../theme';

/* global ymaps */

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function fmtTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDuration(ms) {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}s ${m}d`;
  return `${m} daqiqa`;
}

const GAP_THRESHOLD = 5 * 60 * 1000;

// ===== YANDEX TRAJECTORY MAP =====
function TrajectoryMap({ locations, segments, gaps, selectedPoint, onSelectPoint, height = 520 }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const objectsRef = useRef([]);

  useEffect(() => {
    if (!locations.length || typeof ymaps === 'undefined') return;
    ymaps.ready(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }

      const map = new ymaps.Map(mapRef.current, {
        center: [locations[0].latitude, locations[0].longitude],
        zoom: 15,
        controls: ['zoomControl'],
      }, { suppressMapOpenBlock: true });

      mapInstanceRef.current = map;
      renderObjects(map);
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }
    };
  }, [locations, segments, gaps]);

  // Selected point highlight
  useEffect(() => {
    if (!mapInstanceRef.current || selectedPoint === null || !locations[selectedPoint]) return;
    const loc = locations[selectedPoint];
    mapInstanceRef.current.setCenter([loc.latitude, loc.longitude], undefined, { duration: 300 });
  }, [selectedPoint]);

  const renderObjects = (map) => {
    objectsRef.current.forEach(obj => map.geoObjects.remove(obj));
    objectsRef.current = [];

    // Faol segmentlar — yashil chiziq
    segments.forEach(seg => {
      const coords = seg.points.map(p => [p.latitude, p.longitude]);
      const polyline = new ymaps.Polyline(coords, {}, {
        strokeColor: '#10B981',
        strokeWidth: 4,
        strokeOpacity: 0.9,
      });
      map.geoObjects.add(polyline);
      objectsRef.current.push(polyline);
    });

    // Uzilish chiziqlari — qizil punktir
    gaps.forEach(gap => {
      const polyline = new ymaps.Polyline([
        [gap.from.latitude, gap.from.longitude],
        [gap.to.latitude, gap.to.longitude],
      ], {}, {
        strokeColor: '#EF4444',
        strokeWidth: 3,
        strokeOpacity: 0.7,
        strokeStyle: 'dash',
      });
      map.geoObjects.add(polyline);
      objectsRef.current.push(polyline);

      // Uzilish markeri
      const midLat = (gap.from.latitude + gap.to.latitude) / 2;
      const midLng = (gap.from.longitude + gap.to.longitude) / 2;
      const gapMark = new ymaps.Placemark([midLat, midLng], {
        balloonContent: `<div style="font-family:Nunito,sans-serif"><b style="color:#DC2626">📡 Signal uzilishi</b><br>Davomiyligi: <b>${fmtDuration(gap.duration)}</b><br><span style="font-size:12px;color:#666">${fmtTime(gap.from.timestamp)} — ${fmtTime(gap.to.timestamp)}</span></div>`,
      }, {
        iconLayout: 'default#imageWithContent',
        iconImageHref: '',
        iconImageSize: [36, 36],
        iconImageOffset: [-18, -18],
        iconContentLayout: ymaps.templateLayoutFactory.createClass(
          '<div style="width:36px;height:36px;border-radius:50%;background:#FEF2F2;border:2px solid #EF4444;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 8px rgba(239,68,68,0.3)">📡</div>'
        ),
      });
      map.geoObjects.add(gapMark);
      objectsRef.current.push(gapMark);
    });

    // Nuqtalar
    locations.forEach((loc, i) => {
      const isFirst = i === 0;
      const isLast = i === locations.length - 1;

      if (isFirst || isLast) {
        const emoji = isFirst ? '🟢' : '🔴';
        const clr = isFirst ? '#10B981' : '#EF4444';
        const pm = new ymaps.Placemark([loc.latitude, loc.longitude], {
          balloonContent: `<div style="font-family:Nunito,sans-serif"><b>${emoji} ${isFirst ? "Boshlang'ich" : 'Oxirgi'} nuqta</b><br>${fmtTime(loc.timestamp)}${loc.address ? '<br>' + loc.address : ''}</div>`,
        }, {
          iconLayout: 'default#imageWithContent',
          iconImageHref: '',
          iconImageSize: [38, 38],
          iconImageOffset: [-19, -38],
          iconContentLayout: ymaps.templateLayoutFactory.createClass(
            `<div style="width:38px;height:38px;border-radius:50%;background:${clr};border:3px solid #fff;box-shadow:0 4px 14px ${clr}60;display:flex;align-items:center;justify-content:center;font-size:17px">${emoji}</div>`
          ),
        });
        pm.events.add('click', () => onSelectPoint(i));
        map.geoObjects.add(pm);
        objectsRef.current.push(pm);
      } else {
        const circle = new ymaps.Circle([[loc.latitude, loc.longitude], 6], {
          balloonContent: `<div style="font-family:Nunito,sans-serif"><b>📍 #${i + 1}</b><br>${fmtTime(loc.timestamp)}<br>Tezlik: ${(loc.speed || 0).toFixed(1)} km/h<br>Aniqlik: ${(loc.accuracy || 0).toFixed(0)}m</div>`,
        }, {
          fillColor: '#10B981',
          strokeColor: '#10B981',
          fillOpacity: 0.7,
          strokeWidth: 1.5,
        });
        circle.events.add('click', () => onSelectPoint(i));
        map.geoObjects.add(circle);
        objectsRef.current.push(circle);
      }
    });

    // Fit bounds
    if (locations.length > 1) {
      const bounds = map.geoObjects.getBounds();
      if (bounds) map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40, duration: 500 });
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: T.shadow, border: `2px solid ${T.border}` }}>
      <div ref={mapRef} style={{ height, width: '100%' }} />
      {/* Legend */}
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 20, fontSize: 13, fontWeight: 700, color: T.textSec, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 24, height: 4, background: '#10B981', borderRadius: 2, display: 'inline-block' }} /> Harakat
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 24, height: 0, borderTop: '3px dashed #EF4444', display: 'inline-block' }} /> Uzilish
        </span>
        <span>🟢 Boshlanish</span>
        <span>🔴 Tugash</span>
        <span>📡 Signal yo'qolgan</span>
      </div>
    </div>
  );
}

// ===== MAIN PAGE =====
export default function TrajectoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(todayStr);
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState(todayStr);
  const [toTime, setToTime] = useState(today.toTimeString().slice(0, 5));
  const [selectedPoint, setSelectedPoint] = useState(null);

  useEffect(() => {
    childAPI.get(id).then(r => setChild(r.data)).catch(() => navigate('/children'));
  }, [id]);

  useEffect(() => {
    if (child?.device_info) loadTrajectory();
  }, [child]);

  const loadTrajectory = async () => {
    if (!child?.device_info) return;
    setLoading(true); setError('');
    try {
      const from = `${fromDate}T${fromTime}:00`;
      const to = `${toDate}T${toTime}:59`;
      const r = await parentDeviceAPI.locations(child.device_info.id, { from_date: from, to_date: to });
      const sorted = (r.data || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setLocations(sorted);
      if (sorted.length === 0) setError("Bu vaqt oralig'ida GPS ma'lumoti topilmadi");
    } catch (e) { setError('Xatolik: ' + (e.message || "Ma'lumot olishda xato")); }
    finally { setLoading(false); }
  };

  const { segments, gaps, stats } = useMemo(() => {
    if (locations.length < 2) return { segments: [], gaps: [], stats: null };
    const segs = []; const gps = [];
    let currentSeg = [locations[0]];
    let totalDist = 0, gapCount = 0, gapDuration = 0;

    for (let i = 1; i < locations.length; i++) {
      const prev = locations[i - 1], curr = locations[i];
      const timeDiff = new Date(curr.timestamp) - new Date(prev.timestamp);
      const dist = haversine(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
      if (timeDiff > GAP_THRESHOLD) {
        if (currentSeg.length > 0) segs.push({ points: [...currentSeg], type: 'active' });
        gps.push({ from: prev, to: curr, duration: timeDiff });
        gapCount++; gapDuration += timeDiff;
        currentSeg = [curr];
      } else { totalDist += dist; currentSeg.push(curr); }
    }
    if (currentSeg.length > 0) segs.push({ points: [...currentSeg], type: 'active' });
    const first = new Date(locations[0].timestamp), last = new Date(locations[locations.length - 1].timestamp);
    return {
      segments: segs, gaps: gps,
      stats: {
        totalPoints: locations.length, totalDist, totalTime: last - first,
        activeTime: (last - first) - gapDuration, gapCount, gapDuration,
        avgSpeed: locations.reduce((s, l) => s + (l.speed || 0), 0) / locations.length,
        firstTime: first, lastTime: last,
      }
    };
  }, [locations]);

  const inp = {
    padding: '10px 14px', background: '#fff', border: `2px solid ${T.border}`,
    borderRadius: 12, fontSize: 14, fontFamily: T.mono, fontWeight: 600,
    color: T.text, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 14, color: T.textMuted, flexWrap: 'wrap' }}>
        <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/children')}>Bolalar</span>
        <span>→</span>
        <span style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/children/' + id)}>{child?.name || '...'}</span>
        <span>→</span>
        <span style={{ fontWeight: 800, color: T.text }}>🗺 Traektoriya</span>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 18, padding: '20px 24px', boxShadow: T.shadow, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 20 }}>📅</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Vaqt oralig'ini tanlang</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.bg, padding: '8px 14px', borderRadius: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: T.textMuted }}>DAN</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inp} />
            <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} style={{ ...inp, width: 110 }} />
          </div>
          <span style={{ fontSize: 20, color: T.textMuted }}>→</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.bg, padding: '8px 14px', borderRadius: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: T.textMuted }}>GACHA</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inp} />
            <input type="time" value={toTime} onChange={e => setToTime(e.target.value)} style={{ ...inp, width: 110 }} />
          </div>
          <button onClick={loadTrajectory} disabled={loading} style={{
            padding: '12px 28px', background: loading ? '#E5E7EB' : `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
            color: loading ? '#9CA3AF' : '#fff', border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 800, cursor: loading ? 'default' : 'pointer', fontFamily: T.font,
            boxShadow: loading ? 'none' : `0 4px 16px ${T.blue}40`,
          }}>{loading ? '⟳ Yuklanmoqda...' : "🔍 Ko'rsatish"}</button>

          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { label: 'Bugun', fn: () => { setFromDate(todayStr); setFromTime('00:00'); setToDate(todayStr); setToTime(today.toTimeString().slice(0,5)); }},
              { label: 'Oxirgi 1 soat', fn: () => {
                const h = new Date(Date.now() - 3600000);
                setFromDate(h.toISOString().slice(0,10)); setFromTime(h.toTimeString().slice(0,5));
                setToDate(todayStr); setToTime(today.toTimeString().slice(0,5));
              }},
              { label: 'Kecha', fn: () => {
                const y = new Date(Date.now() - 86400000); const ys = y.toISOString().slice(0,10);
                setFromDate(ys); setFromTime('00:00'); setToDate(ys); setToTime('23:59');
              }},
            ].map((b, i) => (
              <button key={i} onClick={b.fn} style={{
                padding: '8px 14px', background: T.bg, border: `1.5px solid ${T.border}`,
                borderRadius: 10, fontSize: 12, fontWeight: 700, color: T.textSec,
                cursor: 'pointer', fontFamily: T.font,
              }}>{b.label}</button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '14px 20px', background: '#FEF2F2', borderRadius: 14, marginBottom: 20,
          border: '1px solid #FCA5A5', fontSize: 14, fontWeight: 700, color: '#DC2626' }}>⚠️ {error}</div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { icon: '📍', label: 'Nuqtalar', value: stats.totalPoints, color: T.blue },
            { icon: '📏', label: 'Masofa', value: stats.totalDist < 1 ? `${(stats.totalDist * 1000).toFixed(0)}m` : `${stats.totalDist.toFixed(1)}km`, color: T.blue },
            { icon: '⏱', label: 'Davomiyligi', value: fmtDuration(stats.totalTime), color: T.text },
            { icon: '🚶', label: "O'rt. tezlik", value: `${stats.avgSpeed.toFixed(1)} km/h`, color: T.text },
            { icon: '📡', label: 'Uzilishlar', value: stats.gapCount, color: stats.gapCount > 0 ? T.coral : T.blue },
            { icon: '⏸', label: 'Uzilish vaqti', value: stats.gapDuration > 0 ? fmtDuration(stats.gapDuration) : '—', color: stats.gapDuration > 0 ? T.orange : T.text },
          ].map((s, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '14px 16px', boxShadow: T.shadow, textAlign: 'center' }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: s.color, fontFamily: T.mono }}>{s.value}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Map + Timeline */}
      {locations.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 320px', gap: 20, alignItems: 'start' }}>
          <TrajectoryMap
            locations={locations} segments={segments} gaps={gaps}
            selectedPoint={selectedPoint} onSelectPoint={setSelectedPoint}
          />

          {/* Timeline */}
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: T.shadow, overflow: 'hidden', maxHeight: 580 }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${T.border}`, background: T.bg }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>📋 Xronologiya</span>
              <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, marginLeft: 8 }}>{locations.length} nuqta</span>
            </div>
            <div style={{ overflowY: 'auto', maxHeight: 520, padding: '8px 0' }}>
              {locations.map((loc, i) => {
                const isFirst = i === 0, isLast = i === locations.length - 1;
                const isSelected = selectedPoint === i;
                const isGapBefore = i > 0 && (new Date(loc.timestamp) - new Date(locations[i-1].timestamp)) > GAP_THRESHOLD;

                return (
                  <React.Fragment key={loc.id || i}>
                    {isGapBefore && (
                      <div style={{ padding: '6px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 1, background: '#FCA5A5' }} />
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#DC2626', background: '#FEF2F2',
                          padding: '3px 10px', borderRadius: 8, whiteSpace: 'nowrap' }}>
                          📡 {fmtDuration(new Date(loc.timestamp) - new Date(locations[i-1].timestamp))} uzilish
                        </span>
                        <div style={{ flex: 1, height: 1, background: '#FCA5A5' }} />
                      </div>
                    )}
                    <div onClick={() => setSelectedPoint(i)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px',
                      cursor: 'pointer', transition: 'background 0.15s',
                      background: isSelected ? `${T.blue}10` : 'transparent',
                      borderLeft: isSelected ? `3px solid ${T.blue}` : '3px solid transparent',
                    }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                        background: isFirst ? '#10B981' : isLast ? '#EF4444' : isGapBefore ? '#F59E0B' : '#D1D5DB',
                        border: isSelected ? `2px solid ${T.blue}` : 'none',
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: T.mono, color: T.text }}>
                            {fmtTime(loc.timestamp)}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted }}>
                            {(loc.speed || 0).toFixed(1)} km/h
                          </span>
                        </div>
                        {(isFirst || isLast || isSelected) && loc.address && (
                          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.address}</div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!loading && locations.length === 0 && !error && (
        <div style={{ background: '#fff', borderRadius: 18, padding: '60px 40px', boxShadow: T.shadow, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🗺</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8 }}>Traektoriyani ko'rish</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.textMuted }}>Yuqoridagi filtrdan vaqt oralig'ini tanlang va "Ko'rsatish" tugmasini bosing</div>
        </div>
      )}
    </div>
  );
}
